import { useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * The signed-in session.
 *
 * Signing in exchanges the username and password for the backend's API token,
 * which is then sent on every request. Because the gate is the token — not a
 * flag in the UI — clearing localStorage does not get anyone past it; the API
 * simply answers 401.
 */

const STORAGE_KEY = 'algo-os-session';

export interface Session {
  token: string;
  displayName: string;
}

function read(): Session | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed.token !== 'string' || parsed.token.length === 0) return null;
    return { token: parsed.token, displayName: parsed.displayName ?? '' };
  } catch {
    return null;
  }
}

let session: Session | null = typeof window === 'undefined' ? null : read();
const listeners = new Set<() => void>();

// Point the client at the stored token before the first request goes out.
if (session) api.configure({ token: session.token });

function emit(next: Session | null): void {
  session = next;
  api.configure({ token: next?.token ?? '' });
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage being unavailable must not break the running session.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // A sign-out in one tab signs the others out too.
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) {
      session = read();
      api.configure({ token: session?.token ?? '' });
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export async function signIn(username: string, password: string): Promise<void> {
  const result = await api.login({ username, password });
  emit({ token: result.token, displayName: result.displayName });
}

export function signOut(): void {
  emit(null);
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, () => session, () => null);
}
